/**
 * API Configuration
 *
 * Supports development, staging, and production environments.
 */

export type Environment = 'development' | 'staging' | 'production';

// Environment detection based on hostname
export const getEnvironment = (): Environment => {
  if (typeof window === 'undefined') {
    // Server-side: Use NODE_ENV or default to staging
    return (process.env.NODE_ENV as Environment) || 'staging';
  }

  const hostname = window.location.hostname;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }

  // Production domains
  if (hostname === 'app.sequenceplanner.com' || hostname === 'sequenceplanner.com') {
    return 'production';
  }

  // Everything else is staging
  return 'staging';
};

// Get the API base URL
export const getApiBaseUrl = (): string => {
  const env = getEnvironment();

  switch (env) {
    case 'development':
      return 'http://localhost:3001';
    case 'production':
      return 'https://app.sequenceplanner.com';
    case 'staging':
      return 'https://staging.sequenceplanner.com';
  }
};

// Auth uses the same base URL
export const getAuthApiBaseUrl = (): string => {
  return getApiBaseUrl();
};

// Helper for constructing full API URLs
export const buildApiUrl = (endpoint: string): string => {
  const baseUrl = getApiBaseUrl();
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${normalizedEndpoint}`;
};

// Build request headers
export const buildRequestHeaders = (): HeadersInit => {
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
};

// Debug helper
export const getCurrentConfig = () => {
  const environment = getEnvironment();
  const apiUrl = getApiBaseUrl();

  return {
    environment,
    apiUrl,
    isDevelopment: environment === 'development',
    isStaging: environment === 'staging',
    isProduction: environment === 'production',
  };
}; 